import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { useAppSelector } from '../../../store';
import { selectStationAssignment } from '../../../store/slices/stationAssignmentSlice';
import AssociateSidebar from './components/AssociateSidebar';
import MetricsBar from './components/MetricsBar';
import ProcessPathSection from './components/ProcessPathSection';
import { getAssignments } from './api';
import {
  AssignmentAssociate,
  GetAssignmentsResponse,
  ProcessPath,
  Workstation,
} from './models';
import {
  assignAssociateToWorkstation,
  filterUnassignedAssociates,
  isAssigned,
  recomputeMetrics,
  removeAssociateFromWorkstation,
} from './utils';
import './index.scss';

const DEFAULT_HALF = 'first';

/**
 * Full Assignments tab (Tab 2 of the Station Assignment workflow).
 *
 * Responsibilities:
 *   - Fetch the current shift's assignment data on mount (and whenever the
 *     shift / half selectors change) via `getAssignments`.
 *   - Hold the full `GetAssignmentsResponse` in local state as the single
 *     source of truth; all derived values (unassigned pool, metrics) are
 *     computed off of that response.
 *   - Delegate all assignment / removal logic to the pure helpers in
 *     `utils.ts`, re-running `recomputeMetrics` after every mutation so
 *     the MetricsBar stays in sync.
 *   - Compose the layout from the previously built presentational pieces:
 *     `AssociateSidebar`, `MetricsBar`, `ProcessPathSection`.
 *
 * Assignment picker:
 *   When the sidebar fires `onAssignClick(associateId)`, this component
 *   opens a small modal listing every direct-path workstation. Selecting
 *   one invokes `assignAssociateToWorkstation` and closes the modal.
 *
 * This component is intentionally a thin renderer plus effect runner;
 * everything interesting lives in `utils.ts` and the child components,
 * both of which are independently tested.
 */
const AssignmentsPage: React.FC = () => {
  const { selectedDate, selectedShift, planId } =
    useAppSelector(selectStationAssignment);

  const [response, setResponse] = useState<GetAssignmentsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>(
    {},
  );

  // Shift/half selectors in the header. `shift` is seeded from Redux so the
  // first fetch reuses the same clock-window the user selected on the
  // generation page; `half` is a UI-only selector (no Redux counterpart).
  const [shift, setShift] = useState<string>(selectedShift ?? 'day');
  const [half, setHalf] = useState<string>(DEFAULT_HALF);

  // Workstation-picker modal state. `assigningAssociateId` doubles as the
  // modal's open flag: null means closed.
  const [assigningAssociateId, setAssigningAssociateId] = useState<
    string | null
  >(null);

  /**
   * Fetch assignments for the current selection. Wrapped in useCallback
   * so the effect below only re-runs when inputs actually change.
   */
  const fetchData = useCallback(async () => {
    if (!selectedDate || !planId) {
      setError('Missing required parameters (date or planId).');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const clockWindow = (shift || '').toUpperCase();
      const result = await getAssignments(selectedDate, clockWindow, planId);

      // Treat an empty response (no associates and no lastModifiedBy) as an
      // error surface, mirroring the Shift Rotation page's behaviour.
      if (
        result.associates.length === 0 &&
        result.processPaths.length === 0 &&
        !result.lastModifiedBy
      ) {
        setError('Failed to load assignments data. Please try again.');
        setResponse(null);
      } else {
        setResponse(result);
        // Default every process path to expanded on (re-)load.
        const defaultExpanded: Record<string, boolean> = {};
        result.processPaths.forEach((path) => {
          defaultExpanded[path.processPathId] = true;
        });
        setExpandedPaths(defaultExpanded);
      }
    } catch (err: any) {
      setError(err?.message || 'An unexpected error occurred.');
      setResponse(null);
    } finally {
      setLoading(false);
    }
    // `half` is included so changing it re-triggers the fetch (Req 14.2),
    // even though it isn't currently part of the request payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, planId, shift, half]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Derived values ──────────────────────────────────────────────────────

  const processPaths: ProcessPath[] = response?.processPaths ?? [];

  /**
   * Compute the unassigned pool from the authoritative path/workstation
   * structure so the sidebar always reflects the latest state without
   * relying on the `assignedWorkstationId` convenience marker.
   */
  const unassignedAssociates: AssignmentAssociate[] = useMemo(() => {
    if (!response) return [];
    return response.associates.filter(
      (a) => !isAssigned(a, response.processPaths),
    );
  }, [response]);

  const filteredUnassignedAssociates = useMemo(
    () => filterUnassignedAssociates(unassignedAssociates, searchQuery),
    [unassignedAssociates, searchQuery],
  );

  /**
   * Every direct-path workstation flattened into `(processPath, workstation)`
   * pairs for the assignment picker modal.
   */
  const allWorkstations: Array<{
    processPath: ProcessPath;
    workstation: Workstation;
  }> = useMemo(() => {
    const out: Array<{ processPath: ProcessPath; workstation: Workstation }> = [];
    processPaths.forEach((path) => {
      path.workstations.forEach((ws) => {
        out.push({ processPath: path, workstation: ws });
      });
    });
    return out;
  }, [processPaths]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleAssignClick = (associateId: string) => {
    setAssigningAssociateId(associateId);
  };

  const handleCloseAssignModal = () => {
    setAssigningAssociateId(null);
  };

  const handleWorkstationPick = (workstationId: string) => {
    if (!response || !assigningAssociateId) {
      setAssigningAssociateId(null);
      return;
    }
    const mutated = assignAssociateToWorkstation(
      response,
      assigningAssociateId,
      workstationId,
    );
    const nextMetrics = recomputeMetrics(mutated);
    setResponse({ ...mutated, metrics: nextMetrics });
    setAssigningAssociateId(null);
  };

  /**
   * Shared remove callback for both WorkstationCard (locationId is a
   * workstationId) and IndirectAssociateRow (locationId is a processPathId).
   * The pure helper resolves either kind of id.
   */
  const handleAssociateRemove = (associateId: string, locationId: string) => {
    if (!response) return;
    const mutated = removeAssociateFromWorkstation(
      response,
      associateId,
      locationId,
    );
    const nextMetrics = recomputeMetrics(mutated);
    setResponse({ ...mutated, metrics: nextMetrics });
  };

  const handleAddAssociate = (workstationId: string) => {
    // Selecting an associate to place into a specific workstation is the
    // inverse flow of the sidebar's "Assign to…" picker. For now the UI
    // surfaces the picker globally; a future iteration can pre-select the
    // target workstation here.
    // eslint-disable-next-line no-console
    console.debug('Add associate requested for workstation', workstationId);
  };

  const handleToggleExpanded = (processPathId: string) => {
    setExpandedPaths((prev) => ({
      ...prev,
      [processPathId]: !prev[processPathId],
    }));
  };

  const handleShiftChange = (value: string) => {
    setShift(value);
  };

  const handleHalfChange = (value: string) => {
    setHalf(value);
  };

  const handleAssociateViewClick = () => {
    // Placeholder for a future view-mode toggle. Rendered so the button is
    // discoverable in the header (Req 14.3).
  };

  const handleVerifyClick = () => {
    // "Verify" persistence is future work; the button is rendered so the
    // footer matches the design (Req 4.6).
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box className="assignments-page" spacingInset="400">
        <div
          className="assignments-page__loading"
          data-testid="assignments-loader"
        >
          <Loader />
        </div>
      </Box>
    );
  }

  if (error || !response) {
    return (
      <Box className="assignments-page" spacingInset="400">
        <Alert type="error" data-testid="assignments-error">
          {error ?? 'Failed to load assignments data.'}
        </Alert>
      </Box>
    );
  }

  const assigningAssociate: AssignmentAssociate | null = assigningAssociateId
    ? response.associates.find(
        (a) => a.associateId === assigningAssociateId,
      ) ?? null
    : null;

  return (
    <Box className="assignments-page" spacingInset="400">
      <div className="assignments-page__layout">
        {/* Left column: unassigned associate sidebar. */}
        <div className="assignments-page__sidebar">
          <AssociateSidebar
            unassignedAssociates={filteredUnassignedAssociates}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onAssignClick={handleAssignClick}
          />
        </div>

        {/* Right column: header, metrics bar, stacked process path sections. */}
        <div className="assignments-page__main">
          <div
            className="assignments-page__header"
            data-testid="assignments-header"
          >
            <div className="assignments-page__header-selectors">
              <Select
                value={shift}
                onChange={handleShiftChange}
                placeholder="Shift"
                data-testid="assignments-shift-select"
              >
                <SelectOption value="day" label="Day shift" />
                <SelectOption value="night" label="Night shift" />
              </Select>
              <Select
                value={half}
                onChange={handleHalfChange}
                placeholder="Half"
                data-testid="assignments-half-select"
              >
                <SelectOption value="first" label="First half" />
                <SelectOption value="second" label="Second half" />
              </Select>
            </div>
            <Button
              type="secondary"
              onClick={handleAssociateViewClick}
              data-testid="assignments-associate-view-button"
            >
              Associate view
            </Button>
          </div>

          <MetricsBar metrics={response.metrics} />

          <div className="assignments-page__sections">
            {processPaths.map((path) => (
              <ProcessPathSection
                key={path.processPathId}
                processPath={path}
                expanded={expandedPaths[path.processPathId] ?? true}
                onToggleExpanded={handleToggleExpanded}
                onAssociateRemove={handleAssociateRemove}
                onAddAssociate={handleAddAssociate}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Footer: Last Modified info + Verify action. */}
      <div className="assignments-page__footer">
        <Text color="secondary" data-testid="assignments-last-modified">
          Last Modified: {response.lastModifiedDate} ({response.lastModifiedBy})
        </Text>
        <div className="assignments-page__footer-actions">
          <Button
            type="primary"
            onClick={handleVerifyClick}
            data-testid="assignments-verify-button"
          >
            Verify
          </Button>
        </div>
      </div>

      {/* Workstation picker modal. Opens when the sidebar's "Assign to…"
          button fires; closes on workstation pick or explicit cancel. */}
      <Modal
        open={assigningAssociateId !== null}
        onClose={handleCloseAssignModal}
        title="Assign to workstation"
        scrollContainer="viewport"
      >
        <Box
          className="assignments-page__assign-modal-body"
          data-testid="assignments-assign-modal"
        >
          {assigningAssociate && (
            <Text type="b300">
              Select a workstation for {assigningAssociate.associateName}:
            </Text>
          )}
          <div className="assignments-page__assign-modal-list">
            {allWorkstations.map(({ processPath, workstation }) => (
              <Button
                key={workstation.workstationId}
                type="tertiary"
                onClick={() => handleWorkstationPick(workstation.workstationId)}
                data-testid={`assignments-pick-${workstation.workstationId}`}
              >
                {processPath.processPathName} — {workstation.workstationName}
              </Button>
            ))}
          </div>
        </Box>
        <ModalFooter>
          <Button type="secondary" onClick={handleCloseAssignModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Box>
  );
};

export default AssignmentsPage;

import './index.scss';